import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'
import appConfig from '@/configs/app.config'

import enCommon from './lang/en/common.json'
import enEditor from './lang/en/editor.json'
import enBlocks from './lang/en/blocks.json'
import enDialogs from './lang/en/dialogs.json'
import enPanels from './lang/en/panels.json'
import enDocuments from './lang/en/documents.json'
import enShortcuts from './lang/en/shortcuts.json'
import enErrors from './lang/en/errors.json'
import enAuth from './lang/en/auth.json'
import enMobile from './lang/en/mobile.json'
import enSharing from './lang/en/sharing.json'
import enShell from './lang/en/shell.json'
import enSettings from './lang/en/settings.json'
import enHelp from './lang/en/help.json'

import frCommon from './lang/fr/common.json'
import frEditor from './lang/fr/editor.json'
import frBlocks from './lang/fr/blocks.json'
import frDialogs from './lang/fr/dialogs.json'
import frPanels from './lang/fr/panels.json'
import frDocuments from './lang/fr/documents.json'
import frShortcuts from './lang/fr/shortcuts.json'
import frErrors from './lang/fr/errors.json'
import frAuth from './lang/fr/auth.json'
import frMobile from './lang/fr/mobile.json'
import frSharing from './lang/fr/sharing.json'
import frShell from './lang/fr/shell.json'
import frSettings from './lang/fr/settings.json'
import frHelp from './lang/fr/help.json'

import esCommon from './lang/es/common.json'
import esEditor from './lang/es/editor.json'
import esBlocks from './lang/es/blocks.json'
import esDialogs from './lang/es/dialogs.json'
import esPanels from './lang/es/panels.json'
import esDocuments from './lang/es/documents.json'
import esShortcuts from './lang/es/shortcuts.json'
import esErrors from './lang/es/errors.json'
import esAuth from './lang/es/auth.json'
import esMobile from './lang/es/mobile.json'
import esSharing from './lang/es/sharing.json'
import esShell from './lang/es/shell.json'
import esSettings from './lang/es/settings.json'
import esHelp from './lang/es/help.json'

const resources = {
    en: {
        common: enCommon,
        editor: enEditor,
        blocks: enBlocks,
        dialogs: enDialogs,
        panels: enPanels,
        documents: enDocuments,
        shortcuts: enShortcuts,
        errors: enErrors,
        auth: enAuth,
        mobile: enMobile,
        sharing: enSharing,
        shell: enShell,
        settings: enSettings,
        help: enHelp,
    },
    fr: {
        common: frCommon,
        editor: frEditor,
        blocks: frBlocks,
        dialogs: frDialogs,
        panels: frPanels,
        documents: frDocuments,
        shortcuts: frShortcuts,
        errors: frErrors,
        auth: frAuth,
        mobile: frMobile,
        sharing: frSharing,
        shell: frShell,
        settings: frSettings,
        help: frHelp,
    },
    es: {
        common: esCommon,
        editor: esEditor,
        blocks: esBlocks,
        dialogs: esDialogs,
        panels: esPanels,
        documents: esDocuments,
        shortcuts: esShortcuts,
        errors: esErrors,
        auth: esAuth,
        mobile: esMobile,
        sharing: esSharing,
        shell: esShell,
        settings: esSettings,
        help: esHelp,
    },
}

/**
 * The language to start in: the saved choice (the locale store persists it as
 * `locale`), else the browser's when it is one we speak, else the default. Read
 * here, before any module asks for a text: labels computed when a module loads
 * would otherwise be English until the store switched the language.
 */
export function initialLanguage(): string {
    const ours = ['en', 'fr', 'es']
    try {
        const saved = JSON.parse(window.localStorage.getItem('locale') ?? 'null')?.state?.currentLang
        if (typeof saved === 'string' && ours.includes(saved.slice(0, 2))) return saved.slice(0, 2)
    } catch {
        // No storage, or not JSON: fall through.
    }
    try {
        for (const l of navigator.languages ?? [navigator.language]) {
            const base = (l || '').slice(0, 2).toLowerCase()
            if (ours.includes(base)) return base
        }
    } catch {
        // No navigator (tests, server).
    }
    return appConfig.locale
}

i18n.use(initReactI18next).init({
    resources,
    fallbackLng: 'en',
    lng: initialLanguage(),
    defaultNS: 'common',
    ns: ['common', 'editor', 'blocks', 'dialogs', 'panels', 'documents', 'shortcuts', 'errors', 'auth', 'mobile', 'sharing', 'shell', 'settings', 'help'],
    interpolation: {
        escapeValue: false,
    },
})

export const dateLocales: {
    [key: string]: () => Promise<ILocale>
} = {
    en: () => import('dayjs/locale/en'),
    fr: () => import('dayjs/locale/fr'),
    es: () => import('dayjs/locale/es'),
}

export default i18n
